import { useEffect, useState } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { api } from '@/lib/api';
import { trackEvent } from '@/lib/faro';
import { Card, CardContent } from '@/components/ui/card';

export default function SocialAuthCallbackPage() {
    const { provider } = useParams<{ provider: string }>();
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const { setAuth, user } = useAuth();
    const [error, setError] = useState('');

    useEffect(() => {
        const code = searchParams.get('code');
        const state = searchParams.get('state');

        if (!code || !provider) {
            setError('Missing authentication data.');
            return;
        }

        const isLinkFlow = localStorage.getItem('social_link_provider') === provider;
        const hasToken = !!localStorage.getItem('token');

        const params = new URLSearchParams({ code });
        if (state) params.set('state', state);

        if (isLinkFlow && hasToken) {
            // Link flow — user is already authenticated
            localStorage.removeItem('social_link_provider');
            api.get(`/social-accounts/${provider}/link/callback?${params.toString()}`)
                .then(() => {
                    navigate('/profile', { replace: true });
                })
                .catch((err: any) => {
                    setError(err?.message || 'Failed to link account.');
                });
        } else {
            // Login/register flow
            localStorage.removeItem('social_link_provider');
            fetch(`/api/auth/${provider}/callback?${params.toString()}`, {
                headers: { Accept: 'application/json' },
            })
                .then((res) => res.json())
                .then((data) => {
                    if (data.token && data.user) {
                        setAuth(data.user.data ?? data.user, data.token);
                        trackEvent('user_logged_in', { method: provider });
                        navigate(data.is_new ? '/profile' : '/', { replace: true });
                    } else {
                        setError(data.message || 'Authentication failed.');
                    }
                })
                .catch(() => {
                    setError('Authentication failed. Please try again.');
                });
        }
    }, [provider, searchParams, navigate, setAuth]);

    return (
        <Card className="mx-auto max-w-sm mt-20">
            <CardContent className="p-6 text-center">
                {error ? (
                    <div>
                        <p className="text-red-400">{error}</p>
                        <a href="/login" className="mt-3 inline-block text-sm text-violet-400 hover:underline">
                            Back to login
                        </a>
                    </div>
                ) : (
                    <p className="text-slate-400">Authenticating...</p>
                )}
            </CardContent>
        </Card>
    );
}
